/**
 * 0.39.22 — Resolve a bare URL-slug ALIAS to the form's canonical
 * location.
 *
 * A form can be assigned one or more alias UUIDs (in the form's
 * Settings). Visiting the alias at the site root —
 * `caf.govtech.am/<uuid>` — resolves it via the backend and
 * redirects to the canonical language-prefixed URL:
 *   /<lang>/<prefix>/<slug>   (prefix is 'p' or 'services')
 *
 * The alias segment is ONLY treated as an alias when it looks like a
 * UUID, so it never shadows real top-level routes (/about, /faq,
 * /login, /admin, /hy, /en, ...). If it isn't a UUID, or the lookup
 * 404s, we fall through to the home page.
 */
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

// RFC 4122 UUID (any version). We accept any well-formed UUID as an
// alias candidate; the backend decides if it maps to a page.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function AliasResolver() {
  const { alias, lang } = useParams();
  const [target, setTarget] = useState(undefined); // undefined=loading, null=not found, string=redirect

  useEffect(() => {
    let cancelled = false;
    if (!alias || !UUID_RE.test(alias)) {
      setTarget(null);
      return;
    }
    // 0.39.25 - Pass the path language (for /:lang/:alias) so a
    // "no language" alias resolves to that language.
    const q = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    fetch(`/api/pages/resolve-alias/${encodeURIComponent(alias)}${q}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return;
        if (data && data.slug && data.lang) {
          const prefix = data.prefix === 'services' ? 'services' : 'p';
          setTarget(`/${data.lang}/${prefix}/${data.slug}`);
        } else {
          setTarget(null);
        }
      })
      .catch(() => { if (!cancelled) setTarget(null); });
    return () => { cancelled = true; };
  }, [alias, lang]);

  if (target === undefined) return null;               // resolving
  if (target === null) return <Navigate to="/" replace />; // not an alias / not found
  return <Navigate to={target} replace />;             // -> canonical URL
}
